export type ClockTime = {
  hour: number;
  minute: number;
};

export function parseTime(value: string | null | undefined): ClockTime | null {
  if (!value) {
    return null;
  }

  const match = /^(\d{1,2}):(\d{1,2})$/.exec(value);

  if (!match) {
    return null;
  }

  return { hour: Number(match[1]), minute: Number(match[2]) };
}

export function formatTime({ hour, minute }: ClockTime): string {
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

export function compareTime(a: ClockTime, b: ClockTime): number {
  if (a.hour !== b.hour) {
    return a.hour - b.hour;
  }

  return a.minute - b.minute;
}

export type TypedTime = {
  time: ClockTime;
  complete: boolean;
};

export function readTypedTime(
  digits: string,
  minuteStep: number,
): TypedTime | null {
  if (!/^\d+$/.test(digits)) {
    return null;
  }

  const hourLength =
    Number(digits[0]) > 2 ||
    (digits.length > 1 && Number(digits.slice(0, 2)) > 23)
      ? 1
      : 2;

  const hour = Number(digits.slice(0, hourLength));
  const minuteDigits = digits.slice(hourLength, hourLength + 2);
  const minute =
    minuteDigits.length === 1
      ? Number(minuteDigits) * 10
      : Number(minuteDigits || 0);

  if (hour > 23 || minute > 59 || digits.length > hourLength + 2) {
    return null;
  }

  const snapped = Math.min(
    Math.round(minute / minuteStep) * minuteStep,
    60 - minuteStep,
  );

  return {
    time: { hour, minute: snapped },
    complete: minuteDigits.length === 2,
  };
}

export function formatTypedTime(digits: string): string {
  if (digits.length === 1 && Number(digits) <= 2) {
    return `${digits}_:__`;
  }

  const hourLength =
    Number(digits[0]) > 2 ||
    (digits.length > 1 && Number(digits.slice(0, 2)) > 23)
      ? 1
      : 2;

  const hour = digits.slice(0, hourLength).padStart(2, "0");
  const minute = digits.slice(hourLength, hourLength + 2).padEnd(2, "_");

  return digits ? `${hour}:${minute}` : "__:__";
}
